<script>

 // This code and all dependencies taken from: https://github.com/quasarframework/quasar

 /* The MIT License (MIT)

  * Copyright (c) 2015-present Razvan Stoenescu

  * Permission is hereby granted, free of charge, to any person obtaining a copy
  * of this software and associated documentation files (the "Software"), to deal
  * in the Software without restriction, including without limitation the rights
  * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  * copies of the Software, and to permit persons to whom the Software is
  * furnished to do so, subject to the following conditions:

  *        The above copyright notice and this permission notice shall be included in
  * all copies or substantial portions of the Software.

  *                                                  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  *   FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  *   OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
  * THE SOFTWARE. */
 import Vue from 'vue'

 import { getScrollTarget, getScrollHeight, getScrollPosition } from './utils/scroll.js'

 const debounce = function (fn, wait = 250, immediate) {
   let timeout

   function debounced (...args) {
     const later = () => {
       timeout = null
       if (!immediate) {
         fn.apply(this, args)
       }
     }

     clearTimeout(timeout)
     if (immediate && !timeout) {
       fn.apply(this, args)
     }
     timeout = setTimeout(later, wait)
   }

   debounced.cancel = () => {
     clearTimeout(timeout)
   }

   return debounced
 }

 const height = function height (el) {
   return el === window
        ? window.innerHeight
        : el.getBoundingClientRect().height
 }

 const slot = function (vm, slotName) {
   return vm.$scopedSlots[slotName] !== void 0
        ? vm.$scopedSlots[slotName]()
        : void 0
 };
 
 export default {
   name: 'InfiniteScroll',

   props: {
     offset: {
       type: Number,
       default: 500
     },
     scrollTarget: {},
     disable: Boolean,
     reverse: Boolean
   },

   data () {
     return {
       index: 0,
       fetching: false,
       working: true
     }
   },

   watch: {
     disable (val) {
       if (val === true) {
         this.stop()
       }
       else {
         this.resume()
       }
     },

     scrollTarget () {
       this.updateScrollTarget()
     }
   },

   methods: {
     poll () {
       if (this.disable === true || this.fetching === true || this.working === false) {
         return
       }

       const
       scrollHeight = getScrollHeight(this.scrollContainer),
 scrollPosition = getScrollPosition(this.scrollContainer),
 containerHeight = height(this.scrollContainer)

       if (this.reverse === false) {
         if (scrollPosition + containerHeight + this.offset >= scrollHeight) {
           this.trigger()
         }
       }
       else {
         if (scrollPosition < this.offset) {
           this.trigger()
         }
       }
     },

     trigger () {
       if (this.disable === true || this.fetching === true || this.working === false) {
         return
       }

       this.index++
       this.fetching = true

       const heightBefore = getScrollHeight(this.scrollContainer)

       this.$emit('load', this.index, () => {
         if (this.working === true) {
           this.fetching = false
           this.$nextTick(() => {
             if (this.reverse === true) {
               const
               heightAfter = getScrollHeight(this.scrollContainer),
 scrollPosition = getScrollPosition(this.scrollContainer),
 heightDifference = heightAfter - heightBefore

               this.scrollContainer.scrollTop = scrollPosition + heightDifference
             }

             this.$el.closest('body') && this.poll()
           })
         }
       })
     },

     reset () {
       this.index = 0
     },

     resume () {
       if (this.working === false) {
         this.working = true
         this.scrollContainer.addEventListener('scroll', this.poll, {passive: true})
       }
       this.immediatePoll()
     },

     stop () {
       if (this.working === true) {
         this.working = false
         this.fetching = false
         this.scrollContainer.removeEventListener('scroll', this.poll, {passive: true})
       }
     },

     updateScrollTarget () {
       if (this.scrollContainer && this.working === true) {
         this.scrollContainer.removeEventListener('scroll', this.poll, {passive: true})
       }

       if (typeof this.scrollTarget === 'string') {
         this.scrollContainer = document.querySelector(this.scrollTarget)
         if (this.scrollContainer === null) {
           throw new Error(`InfiniteScroll: scroll target container "${this.scrollTarget}" not found`)
           return
         }
       }
       else {
         this.scrollContainer = this.scrollTarget === document.defaultView || this.scrollTarget instanceof Element
                              ? this.scrollTarget
                              : getScrollTarget(this.$el)
       }

       if (this.working === true) {
         this.scrollContainer.addEventListener('scroll', this.poll, {passive: true})
       }
     }
   },

   mounted () {
     this.immediatePoll = this.poll
     this.poll = debounce(this.poll, 100)

     this.updateScrollTarget()
     this.immediatePoll()

     if (this.reverse === true) {
       const
       scrollHeight = getScrollHeight(this.scrollContainer),
 containerHeight = height(this.scrollContainer)

       this.scrollContainer.scrollTop = scrollHeight - containerHeight
     }
   },

   beforeDestroy () {
     if (this.working === true) {
       this.scrollContainer.removeEventListener('scroll', this.poll, {passive: true})
     }
   },

   render (h) {
     const content = this.$scopedSlots.default !== void 0
                   ? this.$scopedSlots.default()
                   : []
     const body = this.fetching === true
                ? [ h('div', { staticClass: 'infinite-scroll__loading' }, slot(this, 'loading')) ]
                : []

     return h(
       'div',
       { staticClass: 'infinite-scroll' },
       this.reverse === false
       ? content.concat(body)
       : body.concat(content)
     )
   }
 }
</script>
